import { supabase } from '../../../integrations/supabase/client';

/**
 * Obtiene el numero_telefono desde agent_channels basándose en el flow_id
 * Esto asegura que el mensaje quede vinculado al canal correcto del agente
 * y NO al último número conectado (que podría ser de otro cliente)
 */
const getPhoneFromAgentChannel = async (flowId: string | null): Promise<string | null> => {
  if (!flowId) return null;

  // Buscar el agente que tiene este workflow activo
  const { data: agent } = await supabase
    .from('agents')
    .select('id')
    .eq('active_workflow_id', flowId)
    .maybeSingle();

  if (!agent) return null;

  // Obtener el número del canal del agente
  const { data: channel } = await supabase
    .from('agent_channels')
    .select('numero_telefono')
    .eq('agent_id', agent.id)
    .limit(1)
    .maybeSingle();

  return channel?.numero_telefono || null;
};

export interface TrainingMessage {
  id?: string;
  user_id?: string;
  agent_id?: string | null;
  flow_id?: string | null;
  numero_telefono?: string | null;
  node_id: string;
  node_type: 'text' | 'image' | 'audio' | 'video' | 'document';
  text_content?: string | null;
  media_url?: string | null;
  file_name?: string | null;
  mime_type?: string | null;
  ai_description?: string | null;
  ai_processing_status?: 'pending' | 'processing' | 'completed' | 'error' | null;
  file_size?: number | null;
  duration?: number | null;
  sequence_order?: number | null;
  group_id?: string | null;
  product_keys?: string[] | null;
  lead_status?: string | null;
  created_at?: string;
  updated_at?: string;
}

export const messagesApi = {
  /**
   * Crea o actualiza un mensaje de entrenamiento
   */
  async upsertMessage(message: TrainingMessage): Promise<TrainingMessage> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuario no autenticado');

    // Obtener el número del canal del agente basándose en el flow_id
    // Esto asegura multi-tenancy correcto
    const numeroTelefono = await getPhoneFromAgentChannel(message.flow_id || null);

    // Buscar si ya existe un mensaje para este node_id + flow_id
    // Esto evita crear duplicados
    let existingId: string | null = null;
    if (message.node_id && message.flow_id) {
      const { data: existingRows } = await supabase
        .from('training_messages')
        .select('id')
        .eq('node_id', message.node_id)
        .eq('flow_id', message.flow_id)
        .order('created_at', { ascending: false });

      if (existingRows && existingRows.length > 0) {
        existingId = existingRows[0].id;

        // Limpiar duplicados: si hay más de una fila, eliminar las extras
        if (existingRows.length > 1) {
          const duplicateIds = existingRows.slice(1).map(r => r.id);
          await supabase
            .from('training_messages')
            .delete()
            .in('id', duplicateIds);
          console.log(`🧹 Eliminados ${duplicateIds.length} mensajes duplicados para node_id: ${message.node_id}`);
        }
      }
    }

    const messageData = {
      ...message,
      id: existingId || message.id || undefined, // Usar ID existente si lo encontramos
      user_id: user.id,
      numero_telefono: numeroTelefono,
      updated_at: new Date().toISOString()
    };

    // Si tenemos un ID existente, actualizar; si no, insertar
    if (existingId) {
      const { data, error } = await supabase
        .from('training_messages')
        .update(messageData)
        .eq('id', existingId)
        .select()
        .single();

      if (error) {
        console.error('Error actualizando mensaje:', error);
        throw error;
      }
      return data as TrainingMessage;
    } else {
      // Insertar nuevo
      const { data, error } = await supabase
        .from('training_messages')
        .insert(messageData)
        .select()
        .single();

      if (error) {
        console.error('Error insertando mensaje:', error);
        throw error;
      }
      return data as TrainingMessage;
    }
  },

  /**
   * Obtiene todos los mensajes de un flujo
   */
  async getMessagesByFlowId(flowId: string): Promise<TrainingMessage[]> {
    const { data, error } = await supabase
      .from('training_messages')
      .select('*')
      .eq('flow_id', flowId)
      .order('sequence_order', { ascending: true });

    if (error) {
      console.error('Error obteniendo mensajes:', error);
      throw error;
    }

    return (data || []) as TrainingMessage[];
  },

  /**
   * Obtiene un mensaje por su node_id
   */
  async getMessageByNodeId(nodeId: string): Promise<TrainingMessage | null> {
    const { data, error } = await supabase
      .from('training_messages')
      .select('*')
      .eq('node_id', nodeId)
      .maybeSingle();

    if (error) {
      console.error('Error obteniendo mensaje:', error);
      throw error;
    }

    return data as TrainingMessage | null;
  },

  /**
   * Elimina un mensaje
   */
  async deleteMessage(nodeId: string): Promise<void> {
    const { error } = await supabase
      .from('training_messages')
      .delete()
      .eq('node_id', nodeId);

    if (error) {
      console.error('Error eliminando mensaje:', error);
      throw error;
    }
  },

  /**
   * Actualiza el estado de procesamiento de IA
   */
  async updateAIStatus(
    nodeId: string,
    status: 'pending' | 'processing' | 'completed' | 'error',
    description?: string
  ): Promise<void> {
    const updateData: any = {
      ai_processing_status: status,
      updated_at: new Date().toISOString()
    };

    if (description !== undefined) {
      updateData.ai_description = description;
    }

    const { error } = await supabase
      .from('training_messages')
      .update(updateData)
      .eq('node_id', nodeId);

    if (error) {
      console.error('Error actualizando estado IA:', error);
      throw error;
    }
  }
};
